import React, { useEffect, useState } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { FaMoneyBillWave, FaUsers, FaEnvelope, FaCalendarAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import './ClubProfile.css';

// IMPORTANT: pentru stiluri în Excel (culori/font), folosește xlsx-js-style
// npm i xlsx-js-style
import * as XLSX from 'xlsx-js-style';

import { saveAs } from 'file-saver';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

export default function ClubProfile() {
  const [clubInfo, setClubInfo] = useState({});
  const [budget, setBudget] = useState({ total: 0, spent: 0, remaining: 0 });
  const [stats, setStats] = useState({ total: 0, athletes: 0, referees: 0, coaches: 0 });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('jwtToken');

  // ✅ doar an curent și următor
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = 2025; y <= currentYear + 1; y++) years.push(y);

  const extractClubIdFromToken = () => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.data?.club_id || payload?.data?.club?.id || null;
    } catch {
      return null;
    }
  };

  // ✅ folosim club-credit.php, care returnează total_paid, total_spent și credit
  const fetchBudgetData = async (clubId, year) => {
    try {
      const res = await fetch(
        `https://www.fro.ro/api/clubs/club-credit.php?club_id=${clubId}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      const total = Number(data.total_paid ?? 0);
      const spent = Number(data.total_spent ?? 0);
      const remaining = Number(data.credit ?? 0);

      setBudget({ total, spent, remaining });
    } catch (err) {
      console.error(err);
      setBudget({ total: 0, spent: 0, remaining: 0 });
    }
  };

  const safeFileName = (name) =>
    (name || 'club')
      .toString()
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '-')
      .replace(/\s+/g, '_');

  const getArray = (json) => {
    if (!json) return [];
    if (Array.isArray(json)) return json;

    // cele mai comune forme întâlnite în API-urile tale
    if (Array.isArray(json.data)) return json.data;
    if (Array.isArray(json.athletes)) return json.athletes;
    if (Array.isArray(json.referees)) return json.referees;
    if (Array.isArray(json.coaches)) return json.coaches;

    // fallback: caută primul câmp care e array
    for (const k of Object.keys(json)) {
      if (Array.isArray(json[k])) return json[k];
    }
    return [];
  };

  const resolveActive = (x) => {
    const candidates = [
      x.is_active,
      x.active,
      x.status_active,
      x.is_valid,
      x.valid,
      x.is_legitimated,
      x.legitimated,
    ];

    const v = candidates.find((v) => v !== undefined && v !== null && v !== '');
    if (v === undefined) return { active: null, label: 'Necunoscut' };

    const s = String(v).toLowerCase().trim();
    const truthy = ['1', 'true', 'da', 'yes', 'active', 'activ'];
    const falsy = ['0', 'false', 'nu', 'no', 'inactive', 'inactiv'];

    if (truthy.includes(s)) return { active: true, label: 'Activ' };
    if (falsy.includes(s)) return { active: false, label: 'Inactiv' };

    const asNum = Number(v);
    if (!Number.isNaN(asNum)) {
      return asNum > 0 ? { active: true, label: 'Activ' } : { active: false, label: 'Inactiv' };
    }

    return { active: null, label: 'Necunoscut' };
  };

  const pick = (x, keys, fallback = '') => {
    for (const k of keys) {
      const v = x?.[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return fallback;
  };

  const toRow = (x, role) => {
    const { active, label } = resolveActive(x);

    const licenseKeysByRole = {
      athlete: ['nr_legitimatie', 'license_number', 'license_no', 'legitimation_no', 'card_number'],
      referee: ['license_number', 'nr_legitimatie', 'license_no', 'legitimation_no', 'card_number'],
      coach: ['license_number', 'nr_legitimatie', 'license_no', 'legitimation_no', 'card_number'],
    };

    const row = {
      Nume: pick(x, ['last_name', 'lastname', 'name_last', 'surname']),
      Prenume: pick(x, ['first_name', 'firstname', 'name_first']),
      'An naștere': pick(x, ['birth_year', 'year_of_birth']),
      Email: pick(x, ['email']),
      Telefon: pick(x, ['phone', 'mobile', 'contact_phone']),
      'Număr legitimație': pick(x, licenseKeysByRole[role] || ['license_number', 'nr_legitimatie']),
      'Status activitate': label,
      Activ: active === null ? '' : active ? 1 : 0,
    };

    // ✅ CNP doar la sportivi — (păstrat aici, dar la export sportivi scriem direct din raw)
    if (role === 'athlete') {
      row.CNP = x?.cnp ?? '';
    }

    // ✅ Tip / Categorie doar la arbitri
    if (role === 'referee') {
      row['Tip / Categorie'] = pick(x, ['categorie', 'category', 'referee_category', 'tip', 'type', 'level'], '-');
    }

    return row;
  };

  const exportMembers = async (yearToExport = selectedYear) => {
    try {
      const clubId = extractClubIdFromToken();
      if (!clubId) {
        alert('Nu pot determina clubul din token.');
        return;
      }

      const cacheBuster = Date.now();

      const [aRes, rRes, cRes] = await Promise.all([
        fetch(
          `https://www.fro.ro/api/athletes/show-athletes-club.php?year=${yearToExport}&_=${cacheBuster}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        fetch(
          `https://www.fro.ro/api/referees/index.php?year=${yearToExport}&_=${cacheBuster}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        fetch(
          `https://www.fro.ro/api/coaches/index.php?year=${yearToExport}&_=${cacheBuster}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      if (!aRes.ok || !rRes.ok || !cRes.ok) {
        throw new Error('Eroare la descărcarea listelor pentru export.');
      }

      const athletesJson = await aRes.json();
      const refereesJson = await rRes.json();
      const coachesJson = await cRes.json();

      const athletesRaw = athletesJson?.data || athletesJson?.athletes || [];
      const refereesRaw = refereesJson?.data || refereesJson?.referees || [];
      const coachesRaw = coachesJson?.data || coachesJson?.coaches || [];

      // ✅ pentru Arbitri/Antrenori păstrăm logica existentă
      const referees = refereesRaw.map((x) => toRow(x, 'referee'));
      const coaches = coachesRaw.map((x) => toRow(x, 'coach'));

      // ✅ HEADERS FIXE (ordine fixă)
      const athleteHeaders = [
        'Nume',
        'Prenume',
        'An naștere',
        'CNP',
        'Email',
        'Telefon',
        'Număr legitimație',
        'Status activitate',
        'Activ',
      ];

      const commonHeaders = [
        'Nume',
        'Prenume',
        'An naștere',
        'Email',
        'Telefon',
        'Număr legitimație',
        'Status activitate',
        'Activ',
      ];

      const refereeHeaders = [...commonHeaders, 'Tip / Categorie'];

      // ====== STILURI EXCEL ======
      const HEADER_BG = '244062'; // fără #
      const WHITE = 'FFFFFF';

      const headerStyle = {
        font: { name: 'Poppins', color: { rgb: WHITE }, bold: true },
        fill: { patternType: 'solid', fgColor: { rgb: HEADER_BG } },
        alignment: { vertical: 'center', horizontal: 'center', wrapText: true },
      };

      const firstColStyle = {
        font: { name: 'Poppins', color: { rgb: WHITE }, bold: true },
        fill: { patternType: 'solid', fgColor: { rgb: HEADER_BG } },
        alignment: { vertical: 'center', horizontal: 'center' },
      };

      const bodyStyle = {
        font: { name: 'Poppins' },
        alignment: { vertical: 'center' },
      };

      const applySheetStyling = (ws, totalRows, totalCols) => {
        const cellAddr = (r, c) => XLSX.utils.encode_cell({ r, c });

        for (let r = 0; r < totalRows; r++) {
          for (let c = 0; c < totalCols; c++) {
            const addr = cellAddr(r, c);
            if (!ws[addr]) continue;

            if (r === 0) {
              ws[addr].s = headerStyle;
              continue;
            }

            if (c === 0) {
              ws[addr].s = firstColStyle;
              continue;
            }

            ws[addr].s = bodyStyle;
          }
        }
      };

      // ✅ helper: transformă obiecte -> AOA (header + rows), cu NR CRT pe prima coloană
      const makeAOA = (rows, headers) => {
        const aoa = [['NR CRT', ...headers]];
        rows.forEach((r, idx) => {
          aoa.push([idx + 1, ...headers.map((h) => (r?.[h] ?? ''))]);
        });
        return aoa;
      };

      // ✅ SPORTIVI: scriem DIRECT din API RAW ca să fie garantat CNP-ul + NR CRT
      const athletesAOA = [
        ['NR CRT', ...athleteHeaders],
        ...athletesRaw.map((x, idx) => {
          const { active, label } = resolveActive(x);
          return [
            idx + 1,
            x?.last_name ?? '',
            x?.first_name ?? '',
            x?.birth_year ?? '',
            String(x?.cnp ?? ''),
            x?.email ?? '',
            x?.phone ?? '',
            x?.nr_legitimatie ?? '',
            label,
            active === null ? '' : active ? 1 : 0,
          ];
        }),
      ];

      const wb = XLSX.utils.book_new();

      // ================== SPORTIVI ==================
      const wsAthletes = XLSX.utils.aoa_to_sheet(athletesAOA);

      // păstrez ideea ta de lățimi, dar acum cu NR CRT în plus
      wsAthletes['!cols'] = [
        { wch: 8 },   // NR CRT
        { wch: 16 },  // Nume
        { wch: 16 },  // Prenume
        { wch: 10 },  // An naștere
        { wch: 18 },  // CNP
        { wch: 26 },  // Email
        { wch: 16 },  // Telefon
        { wch: 16 },  // Număr legitimație
        { wch: 14 },  // Status activitate
        { wch: 8 },   // Activ
      ];

      applySheetStyling(wsAthletes, athletesAOA.length, athletesAOA[0].length);
      XLSX.utils.book_append_sheet(wb, wsAthletes, 'Sportivi');

      // ================== ARBITRI ==================
      const refereesAOA = makeAOA(referees, refereeHeaders);
      const wsReferees = XLSX.utils.aoa_to_sheet(refereesAOA);

      wsReferees['!cols'] = [
        { wch: 8 },   // NR CRT
        { wch: 16 },  // Nume
        { wch: 16 },  // Prenume
        { wch: 10 },  // An naștere
        { wch: 26 },  // Email
        { wch: 16 },  // Telefon
        { wch: 16 },  // Număr legitimație
        { wch: 14 },  // Status activitate
        { wch: 8 },   // Activ
        { wch: 16 },  // Tip / Categorie
      ];

      applySheetStyling(wsReferees, refereesAOA.length, refereesAOA[0].length);
      XLSX.utils.book_append_sheet(wb, wsReferees, 'Arbitri');

      // ================== ANTRENORI ==================
      const coachesAOA = makeAOA(coaches, commonHeaders);
      const wsCoaches = XLSX.utils.aoa_to_sheet(coachesAOA);

      wsCoaches['!cols'] = [
        { wch: 8 },   // NR CRT
        { wch: 16 },  // Nume
        { wch: 16 },  // Prenume
        { wch: 10 },  // An naștere
        { wch: 26 },  // Email
        { wch: 16 },  // Telefon
        { wch: 16 },  // Număr legitimație
        { wch: 14 },  // Status activitate
        { wch: 8 },   // Activ
      ];

      applySheetStyling(wsCoaches, coachesAOA.length, coachesAOA[0].length);
      XLSX.utils.book_append_sheet(wb, wsCoaches, 'Antrenori');

      const clubName = clubInfo?.name || `club_${clubId}`;
      const fileName = `Export_membri_${safeFileName(clubName)}_${yearToExport}_${Date.now()}.xlsx`;

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([excelBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        fileName
      );
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Eroare la export.');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const clubId = extractClubIdFromToken();
      if (!clubId) return;

      setLoading(true);

      // Info club
      const resClub = await fetch(
        `https://www.fro.ro/api/clubs/show.php?id=${clubId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const clubData = await resClub.json();
      setClubInfo(clubData.club || clubData);

      // Credit (buget+cheltuieli)
      await fetchBudgetData(clubId, selectedYear);

      // Membri legitimați
      const cacheBuster = Date.now();
      const [a, r, c] = await Promise.all([
        fetch(`https://www.fro.ro/api/athletes/show-athletes-club.php?year=${selectedYear}&_=${cacheBuster}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`https://www.fro.ro/api/referees/index.php?year=${selectedYear}&_=${cacheBuster}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`https://www.fro.ro/api/coaches/index.php?year=${selectedYear}&_=${cacheBuster}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const athletes = await a.json();
      const referees = await r.json();
      const coaches = await c.json();

      const athletesLegit =
        (athletes.athletes || athletes.data || []).filter(
          (x) => x.is_legitimated == 1).length;
      const refereesLegit =
        (referees.data || []).filter((x) => x.is_legitimated == 1).length;
      const coachesLegit =
        (coaches.data || []).filter((x) => x.is_legitimated == 1).length;

      setStats({
        total: athletesLegit + refereesLegit + coachesLegit,
        athletes: athletesLegit,
        referees: refereesLegit,
        coaches: coachesLegit,
      });

      setLoading(false);
    };

    loadData();
  }, [selectedYear]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Se încarcă datele...</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* SELECTOR AN */}
      <div className="year-selector">
        <FaCalendarAlt className="calendar-icon" />
        <label>An:</label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="year-dropdown"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y === currentYear ? `${y} (curent)` : `${y} `}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="export-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); exportMembers(selectedYear); }}
        >
          Export membri ({selectedYear})
        </button>
      </div>

      {/* CONȚINUT ANIMAT */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedYear}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <div className="cards-grid">
            <div className="cardp">
              <FaMoneyBillWave className="card-icon green" />
              <h3 className="card-title">Credit club ({selectedYear})</h3>
              <p className="card-value">{budget.total.toFixed(2)} lei depus</p>
              <small>{budget.spent.toFixed(2)} lei cheltuit</small>
              <br />
              <small>{budget.remaining.toFixed(2)} lei rămas</small>
            </div>

            <div className="cardp">
              <FaUsers className="card-icon purple" />
              <h3 className="card-title">Membri activi</h3>
              <p className="card-value">{stats.total}</p>
              <small>Sportivi: {stats.athletes}</small>
              <br />
              <small>Arbitri: {stats.referees}</small>
              <br />
              <small>Antrenori: {stats.coaches}</small>
              {stats.total === 0 && (
                <p className="no-members-msg">
                  Niciun membru legitimat pentru anul {selectedYear}.
                </p>
              )}
            </div>

            <div className="cardp">
              <FaEnvelope className="card-icon blue" />
              <h3 className="card-title">Contact</h3>
              <div className="contact-details">
                <p><b>Email:</b> {clubInfo.email}</p>
                <p><b>Tel:</b> {clubInfo.contact_phone}</p>
                <p><b>Persoană:</b> {clubInfo.contact_name}</p>
              </div>
            </div>
          </div>

          {/* GRAFICE */}
          <div className="charts-grid">
            <div className="chart-card">
              <h4>Distribuție credit</h4>
              <Pie
                data={{
                  labels: ['Cheltuit', 'Rămas'],
                  datasets: [
                    {
                      data: [budget.spent, budget.remaining],
                      backgroundColor: ['#525252ff', '#51cf66'],
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }}
              />
            </div>

            <div className="chart-card">
              <h4>Membri legitimați</h4>
              <Bar
                data={{
                  labels: ['Sportivi', 'Arbitri', 'Antrenori'],
                  datasets: [
                    {
                      label: 'Număr membri',
                      data: [stats.athletes, stats.referees, stats.coaches],
                      backgroundColor: ['#339af0', '#845ef7', '#51cf66'],
                    },
                  ],
                }}
                options={{ responsive: true, plugins: { legend: { display: false } } }}
              />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
